import { useEffect, useState } from 'react'
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Bell, X } from 'lucide-react'
import type { Notification, NotificationType } from '@olimhub/shared'
import { notificationsApi } from '@/api'
import { Avatar } from '@/components/profile/Avatar'
import { formatPostTime } from '@/lib/dates'
import { useAuthStore } from '@/store/auth.store'
import { NotificationSkeleton } from '@/components/common'
import { usePushNotifications } from '@/hooks'

const PUSH_DISMISSED_KEY = 'push_prompt_dismissed'


function notificationText(type: NotificationType, t: (k: string) => string): string {
  switch (type) {
    case 'follow': return t('notifications.followed_you')
    case 'boost': return t('notifications.boosted_your_post')
    case 'translation_boost': return t('notifications.translation_boosted')
    case 'mention': return t('notifications.mentioned_you')
    case 'dm': return t('notifications.sent_you_a_message')
    case 'reply': return t('notifications.replied_to_your_post')
    case 'report': return t('notifications.reported_content')
    case 'badge_granted': return t('notifications.badge_granted')
    case 'claps_digest': return t('notifications.claps_digest')
  }
}

function NotificationItem({ notification, onRead }: { notification: Notification; onRead: (id: string) => void }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { account } = useAuthStore()
  const isUnread = !notification.readAt

  function handleClick() {
    if (isUnread) onRead(notification.id)
    switch (notification.type) {
      case 'reply':
        navigate(`/posts/${notification.post?.inReplyToId ?? notification.post?.id}`)
        break
      case 'boost':
      case 'translation_boost':
      case 'mention':
        navigate(`/posts/${notification.post?.id}`)
        break
      case 'follow':
        navigate(`/@${notification.actor.username}`)
        break
      case 'badge_granted':
        navigate(`/@${account?.username ?? notification.actor.username}`)
        break
      case 'dm':
        navigate(`/messages/${notification.actor.username}`)
        break
      case 'claps_digest':
        navigate('/claps')
        break
      default:
        if (notification.post) navigate(`/posts/${notification.post.id}`)
        else navigate(`/@${notification.actor.username}`)
    }
  }

  return (
    <button
      onClick={handleClick}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        width: '100%',
        padding: '14px 16px',
        border: 'none',
        cursor: 'pointer',
        background: isUnread ? 'var(--brand-soft)' : 'transparent',
        borderBottom: '1px solid var(--border)',
        textAlign: 'start',
        borderInlineStart: isUnread ? '3px solid var(--brand)' : '3px solid transparent',
        transition: 'background var(--dur-fast) var(--ease-out)',
      }}
    >
      <Avatar name={notification.actor.displayName} avatarUrl={notification.actor.avatarUrl} animalAvatar={notification.actor.animalAvatar} size="sm" />
      <div style={{ flex: 1, minWidth: 0 }}>
        <p
          style={{
            margin: 0,
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--fs-sm)',
            color: 'var(--text-body)',
            lineHeight: 1.4,
          }}
        >
          <strong style={{ color: 'var(--text-strong)', fontWeight: 700 }}>
            @{notification.actor.username}
          </strong>{' '}
          {notificationText(notification.type, t)}
        </p>
        {notification.post && (
          <p
            style={{
              margin: '4px 0 0',
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--fs-xs)',
              color: 'var(--text-muted)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {notification.post.content}
          </p>
        )}
        <span
          style={{
            display: 'block',
            marginTop: 4,
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--fs-xs)',
            color: 'var(--text-faint)',
          }}
        >
          {formatPostTime(notification.createdAt)}
        </span>
      </div>
    </button>
  )
}

function PushPromptBanner() {
  const { t } = useTranslation()
  const { permission, isSubscribed, isLoading, subscribe } = usePushNotifications()
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(PUSH_DISMISSED_KEY) === '1')

  if (dismissed || isSubscribed || permission === 'granted' || permission === 'denied') return null

  function handleDismiss() {
    localStorage.setItem(PUSH_DISMISSED_KEY, '1')
    setDismissed(true)
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        padding: '14px 16px',
        borderBottom: '1px solid var(--border)',
        background: 'var(--brand-soft)',
      }}
    >
      <Bell size={20} style={{ color: 'var(--brand)', flexShrink: 0, marginTop: 2 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ margin: '0 0 4px', fontFamily: 'var(--font-sans)', fontWeight: 700, fontSize: 'var(--fs-sm)', color: 'var(--text-strong)' }}>
          {t('notifications.push_prompt_title')}
        </p>
        <p style={{ margin: '0 0 10px', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-xs)', color: 'var(--text-body)', lineHeight: 1.4 }}>
          {t('notifications.push_prompt_body')}
        </p>
        <button
          onClick={subscribe}
          disabled={isLoading}
          style={{
            padding: '7px 14px',
            border: 'none',
            borderRadius: 'var(--radius-pill)',
            background: 'var(--brand)',
            color: 'var(--on-brand)',
            fontFamily: 'var(--font-sans)',
            fontWeight: 600,
            fontSize: 'var(--fs-xs)',
            cursor: isLoading ? 'default' : 'pointer',
            opacity: isLoading ? 0.6 : 1,
          }}
        >
          {t('notifications.push_prompt_cta')}
        </button>
      </div>
      <button
        onClick={handleDismiss}
        aria-label={t('notifications.push_prompt_dismiss')}
        style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4, display: 'flex' }}
      >
        <X size={16} />
      </button>
    </div>
  )
}

export function NotificationsPage() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useInfiniteQuery({
    queryKey: ['notifications'],
    queryFn: ({ pageParam }) => notificationsApi.getAll(pageParam as string | undefined),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  })

  const allNotifications = data?.pages.flatMap((p) => p.items) ?? []
  const hasUnread = allNotifications.some((n) => !n.readAt)

  useEffect(() => {
    void queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] })
  }, [queryClient])

  const markOneMutation = useMutation({
    mutationFn: (id: string) => notificationsApi.markOneRead(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] })
      void queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] })
    },
  })

  const markAllMutation = useMutation({
    mutationFn: () => notificationsApi.markAllRead(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] })
      void queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] })
    },
  })

  return (
    <div style={{ maxWidth: 600, margin: '0 auto' }}>
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '14px 16px',
          borderBottom: '1px solid var(--border)',
          background: 'var(--surface)',
        }}
      >
        <h1
          style={{
            margin: 0,
            fontFamily: 'var(--font-sans)',
            fontWeight: 700,
            fontSize: 'var(--fs-body-lg)',
            color: 'var(--text-strong)',
          }}
        >
          {t('notifications.title')}
        </h1>
        {hasUnread && (
          <button
            onClick={() => markAllMutation.mutate()}
            disabled={markAllMutation.isPending}
            style={{
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--fs-sm)',
              fontWeight: 600,
              color: 'var(--brand)',
              padding: '6px 8px',
            }}
          >
            {t('notifications.mark_all_read')}
          </button>
        )}
      </div>

      {!isLoading && allNotifications.length > 0 && <PushPromptBanner />}

      {isLoading ? (
        <>
          <NotificationSkeleton />
          <NotificationSkeleton />
          <NotificationSkeleton />
          <NotificationSkeleton />
          <NotificationSkeleton />
        </>
      ) : allNotifications.length === 0 ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '56px 24px',
            textAlign: 'center',
          }}
        >
          <Bell size={48} strokeWidth={1.5} style={{ color: 'var(--text-faint)', marginBottom: 12 }} />
          <p
            style={{
              margin: 0,
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--fs-sm)',
              fontWeight: 600,
              color: 'var(--text-strong)',
            }}
          >
            {t('notifications.empty')}
          </p>
        </div>
      ) : (
        <>
          {allNotifications.map((notification) => (
            <NotificationItem
              key={notification.id}
              notification={notification}
              onRead={(id) => markOneMutation.mutate(id)}
            />
          ))}

          {hasNextPage && (
            <button
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
              style={{
                display: 'block',
                width: '100%',
                padding: '14px',
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                fontFamily: 'var(--font-sans)',
                fontSize: 'var(--fs-sm)',
                color: 'var(--brand)',
                fontWeight: 600,
              }}
            >
              {isFetchingNextPage ? t('feed.loading_more') : t('feed.load_more')}
            </button>
          )}
        </>
      )}
    </div>
  )
}
